import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { RISK_COMPLIANCE_BLOCK_STATUS } from '../../dataHandlers/riskComplianceConstants';
import {
  getRiskOwnerNameSx,
  getRiskOwnerNationalitySx,
  getRiskOwnerPercentSx,
  getRiskOwnerRowSx,
} from './riskComplianceStyles';
import DnBDataCard, { DnBFieldRow } from './DnBDataCard';

function formatCapitalDetail(detail, labels) {
  if (!detail?.amount && !detail?.type) {
    return null;
  }

  const amount =
    detail.amount != null && detail.currency
      ? `${detail.amount.toLocaleString()} ${detail.currency}`
      : detail.amount?.toLocaleString();
  const date = detail.startDate ? ` · ${detail.startDate}` : '';

  return `${detail.type || labels.capitalType}: ${amount || '—'}${date}`;
}

function formatStockDetail(stock, labels) {
  const parts = [];
  if (stock.stockType) {
    parts.push(stock.stockSubType ? `${stock.stockType} ${stock.stockSubType}` : stock.stockType);
  }
  if (stock.ownershipPercentage != null) {
    parts.push(`${stock.ownershipPercentage}%`);
  }
  if (stock.shareQuantity != null) {
    parts.push(`${stock.shareQuantity} ${labels.shares}`);
  }
  if (stock.hasVotingRights) {
    parts.push(labels.votingRights);
  }

  return parts.join(' · ');
}

export default function OwnershipInsightsCard({
  block,
  labels,
  emptyMessage,
  unavailableMessage,
  noDataMessage,
  fetchLabel,
  onFetch,
  onRefresh,
  isFetching,
}) {
  const theme = useTheme();
  const data = block?.data;
  const owners = data?.beneficialOwners ?? [];
  const capitalDetails = data?.capitalDetails ?? [];

  return (
    <DnBDataCard
      title={labels.title}
      icon={<GroupsOutlinedIcon fontSize="small" color="action" />}
      blockStatus={block?.status || RISK_COMPLIANCE_BLOCK_STATUS.EMPTY}
      emptyMessage={emptyMessage}
      unavailableMessage={unavailableMessage}
      noDataMessage={noDataMessage}
      fetchLabel={fetchLabel}
      onFetch={onFetch}
      onRefresh={onRefresh}
      isFetching={isFetching}
    >
      {capitalDetails.map((detail, index) => (
        <DnBFieldRow
          key={`capital-${index}`}
          label={labels.capitalStructure}
          value={formatCapitalDetail(detail, labels)}
        />
      ))}

      {owners.map((owner, index) => (
        <Box key={`${owner.name}-${index}`} sx={getRiskOwnerRowSx(theme)}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="caption" sx={getRiskOwnerNameSx(theme)} noWrap>
              {owner.name}
            </Typography>
            <Typography variant="caption" sx={getRiskOwnerNationalitySx(theme)}>
              {[owner.subjectType, owner.nationality].filter(Boolean).join(' · ')}
            </Typography>
            {(owner.indirectOwnershipPercentage > 0 || owner.votingPercentageHigh > 0) && (
              <Typography variant="caption" sx={{ display: 'block', color: theme.palette.text.secondary }}>
                {owner.indirectOwnershipPercentage > 0 &&
                  `${labels.indirectOwnership}: ${owner.indirectOwnershipPercentage}%`}
                {owner.indirectOwnershipPercentage > 0 && owner.votingPercentageHigh > 0 && ' · '}
                {owner.votingPercentageHigh > 0 && `${labels.votingRightsPercent}: ${owner.votingPercentageHigh}%`}
              </Typography>
            )}
            {owner.stockDetails?.map((stock, stockIndex) => (
              <Typography
                key={`stock-${index}-${stockIndex}`}
                variant="caption"
                sx={{ display: 'block', color: theme.palette.text.secondary }}
              >
                {formatStockDetail(stock, labels)}
              </Typography>
            ))}
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              px: 1,
              py: 0.375,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="caption" sx={getRiskOwnerPercentSx(theme)}>
              {owner.ownershipPercentage}%
            </Typography>
          </Box>
        </Box>
      ))}
    </DnBDataCard>
  );
}
